'use client'

/**
 * JotaiProvider.tsx
 *
 * Compatibility provider that syncs Jotai atoms with existing contexts
 *
 * Primary responsibilities:
 * - Bridge between React Context and Jotai atoms
 * - Allow gradual migration without breaking existing code
 * - Sync state between contexts and atoms
 */

import React, { useEffect, useContext, useState, useRef } from 'react'
import { Provider, useAtom, useSetAtom } from 'jotai'
import { ChatbotUIContext } from '@/context/context'
import { ChatbotUIChatContext } from '@/context/chat'
import {
  abortControllerAtom,
  chatFilesAtom,
  chatImagesAtom,
  chatMessagesAtom,
  setChatMessagesAtom,
  chatSettingsAtom,
  firstTokenReceivedAtom,
  isGeneratingAtom,
  newMessageFilesAtom,
  newMessageImagesAtom,
  selectedChatAtom,
  selectedChatIdAtom,
  showFilesDisplayAtom,
  userInputAtom
} from '@/atoms/chatAtoms'
import { profileAtom } from '@/atoms/profileAtoms'
import {
  selectedToolsAtom,
  toolInUseAtom,
  platformToolsAtom,
  toolsAtom,
  promptVariablesAtom,
  showPromptVariablesAtom,
  useRetrievalAtom,
  sourceCountAtom
} from '@/atoms/toolsAtoms'
import {
  selectedWorkspaceAtom,
  selectedWorkspaceIdAtom,
  workspacesAtom,
  workspaceImagesAtom
} from '@/atoms/workspaceAtoms'
import { showSidebarAtom, isPaywallOpenAtom } from '@/atoms/uiAtoms'
import { FeatureFlagsProvider } from '@/components/providers/FeatureFlagsProvider'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { appFlags, marketingFlags } from '@/atoms/featureFlagsAtoms'
import {
  preloaderStateAtom,
  cachedHostedModelsAtom,
  cachedLocalModelsAtom,
  cachedOpenRouterModelsAtom,
  lastModelFetchTimestampAtom,
  cachedPlatformToolsAtom,
  preloadPlatformTools
} from '@/lib/preloader'
import {
  availableHostedModelsAtom,
  availableLocalModelsAtom,
  availableOpenRouterModelsAtom,
  allModelsAtom
} from '@/atoms/modelsAtoms'
import { Tables } from '@/supabase/types'
import { useRootMountMarker } from '@/components/utility/root-mount-marker'
import { safeConsole } from '@/lib/utils/safeConsole'

// Import the auth key constant from platform-tools
import { PLATFORM_TOOLS_AUTH_KEY } from '@/db/platform-tools'

/**
 * Provider component that syncs existing React Context with Jotai atoms
 * This allows for gradual migration of components from Context to Atoms
 *
 * Optimized to reduce initial load overhead
 */
export function ContextToJotaiSyncProvider({ children }: { children: React.ReactNode }) {
  // Get state from contexts
  const context = useContext(ChatbotUIContext)
  const chatContext = useContext(ChatbotUIChatContext)

  useRootMountMarker('jotai-sync-provider')

  // Reference to track initialization
  const initRef = useRef({
    syncedInitial: false, // Flag to track if we've done the initial sync
    syncedCritical: false, // Flag to track if we've synced critical data
    syncInProgress: false, // Flag to prevent overlapping syncs
    toolsPreloaded: false // Flag to track if tools were preloaded
  })

  // State to track sync progress
  const [syncStep, setSyncStep] = useState(0)

  // Extract critical state first for immediate UI needs
  const {
    // CRITICAL STATE
    profile,
    selectedWorkspace,
    workspaces,
    showSidebar,

    // The rest will be synced after critical initialization
    ...restOfContext
  } = context

  // Setup all atom setters
  const [, setAtomUserInput] = useAtom(userInputAtom)
  const [, setAtomIsGenerating] = useAtom(isGeneratingAtom)
  const [, setAtomFirstTokenReceived] = useAtom(firstTokenReceivedAtom)
  const [, setAtomAbortController] = useAtom(abortControllerAtom)
  const [, setAtomChatSettings] = useAtom(chatSettingsAtom)
  const [, setAtomSelectedChat] = useAtom(selectedChatAtom)
  const [, setAtomSelectedChatId] = useAtom(selectedChatIdAtom)
  const [, setAtomChatFiles] = useAtom(chatFilesAtom)
  const [, setAtomChatImages] = useAtom(chatImagesAtom)
  const [, setAtomNewMessageFiles] = useAtom(newMessageFilesAtom)
  const [, setAtomNewMessageImages] = useAtom(newMessageImagesAtom)
  const [, setAtomShowFilesDisplay] = useAtom(showFilesDisplayAtom)
  const [, setAtomProfile] = useAtom(profileAtom)
  const [, setAtomTools] = useAtom(toolsAtom)
  const [, setAtomPlatformTools] = useAtom(platformToolsAtom)
  const [, setAtomSelectedTools] = useAtom(selectedToolsAtom)
  const [, setAtomToolInUse] = useAtom(toolInUseAtom)
  const [, setAtomUseRetrieval] = useAtom(useRetrievalAtom)
  const [, setAtomSourceCount] = useAtom(sourceCountAtom)
  const [, setAtomPromptVariables] = useAtom(promptVariablesAtom)
  const [, setAtomShowPromptVariables] = useAtom(showPromptVariablesAtom)
  const [, setAtomWorkspaces] = useAtom(workspacesAtom)
  const [, setAtomSelectedWorkspace] = useAtom(selectedWorkspaceAtom)
  const [, setAtomSelectedWorkspaceId] = useAtom(selectedWorkspaceIdAtom)
  const [, setAtomWorkspaceImages] = useAtom(workspaceImagesAtom)
  const [, setAtomShowSidebar] = useAtom(showSidebarAtom)
  const [, setAtomIsPaywallOpen] = useAtom(isPaywallOpenAtom)
  const setChatMessages = useSetAtom(setChatMessagesAtom)

  // Preloader state atoms
  const [, setPreloaderState] = useAtom(preloaderStateAtom)
  const [, setCachedHostedModels] = useAtom(cachedHostedModelsAtom)
  const [, setCachedLocalModels] = useAtom(cachedLocalModelsAtom)
  const [, setCachedOpenRouterModels] = useAtom(cachedOpenRouterModelsAtom)
  const [, setLastModelFetchTimestamp] = useAtom(lastModelFetchTimestampAtom)
  const [, setAvailableHostedModels] = useAtom(availableHostedModelsAtom)
  const [, setAvailableLocalModels] = useAtom(availableLocalModelsAtom)
  const [, setAvailableOpenRouterModels] = useAtom(availableOpenRouterModelsAtom)
  const [, setAllModels] = useAtom(allModelsAtom)
  const [cachedPlatformTools, setCachedPlatformTools] = useAtom(cachedPlatformToolsAtom)

  // Try to preload platform tools from cache immediately
  useEffect(() => {
    if (initRef.current.toolsPreloaded) return

    // Mark as preloaded to prevent multiple attempts
    initRef.current.toolsPreloaded = true

    // Try to load platform tools from cache
    preloadPlatformTools((tools: Tables<'tools'>[]) => {
      if (tools && tools.length > 0) {
        setAtomPlatformTools(tools)
        setPreloaderState((prev) => ({
          ...prev,
          platformToolsLoaded: true
        }))
      }
    }).catch((error) => {
      safeConsole.warn('Error preloading platform tools:', error)
    })
  }, [setAtomPlatformTools, setPreloaderState])

  // Sync critical data first
  useEffect(() => {
    // Skip if already synced or in progress
    if (initRef.current.syncedCritical || initRef.current.syncInProgress) return

    // Mark sync as in progress
    initRef.current.syncInProgress = true

    // Set critical state
    profile && setAtomProfile(profile)
    workspaces && setAtomWorkspaces(workspaces)
    selectedWorkspace && setAtomSelectedWorkspace(selectedWorkspace)
    setAtomSelectedWorkspaceId(selectedWorkspace?.id || null)
    setAtomShowSidebar(showSidebar)

    // Mark critical sync as complete
    initRef.current.syncedCritical = true
    initRef.current.syncInProgress = false

    // Proceed to next step
    setSyncStep(1)
  }, [
    profile,
    selectedWorkspace,
    workspaces,
    showSidebar,
    setAtomProfile,
    setAtomWorkspaces,
    setAtomSelectedWorkspace,
    setAtomSelectedWorkspaceId,
    setAtomShowSidebar
  ])

  useEffect(() => {
    if (!initRef.current.syncedCritical) {
      return
    }

    setAtomProfile(profile)
    setAtomWorkspaces(workspaces || [])
    setAtomSelectedWorkspace(selectedWorkspace || null)
    setAtomSelectedWorkspaceId(selectedWorkspace?.id || null)
    setAtomShowSidebar(showSidebar)
  }, [
    profile,
    selectedWorkspace,
    showSidebar,
    workspaces,
    setAtomProfile,
    setAtomSelectedWorkspace,
    setAtomSelectedWorkspaceId,
    setAtomShowSidebar,
    setAtomWorkspaces
  ])

  // Sync the rest of the data in a second pass (after critical data is loaded)
  useEffect(() => {
    if (syncStep !== 1 || initRef.current.syncedInitial || initRef.current.syncInProgress) return

    // Mark sync as in progress
    initRef.current.syncInProgress = true

    // Sync remaining data with a small delay to prioritize UI rendering
    const timeoutId = setTimeout(() => {
      // Get userInput from chat context
      const { userInput, chatMessages, isGenerating, firstTokenReceived, abortController } =
        chatContext

      // Get remaining state from the main context
      const {
        chatSettings,
        selectedChat,
        selectedChatId,
        chatFiles,
        chatImages,
        newMessageFiles,
        newMessageImages,
        showFilesDisplay,
        tools,
        platformTools,
        selectedTools,
        toolInUse,
        useRetrieval,
        sourceCount,
        promptVariables,
        showPromptVariables,
        workspaceImages,
        isPaywallOpen
      } = restOfContext as any // Cast to any to avoid type errors with mixed contexts

      // Update atoms with extracted data
      userInput !== undefined && setAtomUserInput(userInput)

      if (chatMessages && selectedChat) {
        // Handle chat messages format
        if (Array.isArray(chatMessages)) {
          setChatMessages({
            chatId: selectedChat.id,
            messages: chatMessages
          })
        } else {
          setChatMessages(chatMessages)
        }
      }

      isGenerating !== undefined && setAtomIsGenerating(isGenerating)
      firstTokenReceived !== undefined && setAtomFirstTokenReceived(firstTokenReceived)
      abortController && setAtomAbortController(abortController)

      chatSettings && setAtomChatSettings(chatSettings)
      selectedChat && setAtomSelectedChat(selectedChat)
      selectedChatId && setAtomSelectedChatId(selectedChatId)

      // Handle potentially mismatched types safely
      if (chatFiles) setAtomChatFiles(chatFiles as any)
      if (chatImages) setAtomChatImages(chatImages)

      newMessageFiles && setAtomNewMessageFiles(newMessageFiles)
      newMessageImages && setAtomNewMessageImages(newMessageImages)
      setAtomShowFilesDisplay(showFilesDisplay)

      tools && setAtomTools(tools)

      // Check authentication state to decide how to handle platformTools
      const isAuthenticated = !!profile?.user_id

      // Only update platformTools if context has newer data or we need to refresh based on auth
      if (platformTools && (!initRef.current.toolsPreloaded || platformTools.length > 0)) {
        setAtomPlatformTools(platformTools)

        // Also store auth state with platform tools
        if (typeof window !== 'undefined') {
          try {
            localStorage.setItem(PLATFORM_TOOLS_AUTH_KEY, String(isAuthenticated))

            // Update window cache auth state
            if (window.__PLATFORM_TOOLS_CACHE) {
              window.__PLATFORM_TOOLS_CACHE.authState = isAuthenticated
            }
          } catch (error) {
            safeConsole.warn('Error updating platform tools auth state:', error)
          }
        }
      }

      selectedTools && setAtomSelectedTools(selectedTools)
      toolInUse && setAtomToolInUse(toolInUse)
      setAtomUseRetrieval(useRetrieval)
      setAtomSourceCount(sourceCount)
      promptVariables && setAtomPromptVariables(promptVariables)
      setAtomShowPromptVariables(showPromptVariables)

      workspaceImages && setAtomWorkspaceImages(workspaceImages)
      setAtomIsPaywallOpen(isPaywallOpen)

      // Mark sync as complete
      initRef.current.syncedInitial = true
      initRef.current.syncInProgress = false

      // Move to next step
      setSyncStep(2)
    }, 200) // Short delay to prioritize UI rendering

    return () => clearTimeout(timeoutId)
  }, [
    syncStep,
    chatContext,
    restOfContext,
    setChatMessages,
    setAtomUserInput,
    setAtomIsGenerating,
    setAtomFirstTokenReceived,
    setAtomAbortController,
    setAtomChatSettings,
    setAtomSelectedChat,
    setAtomSelectedChatId,
    setAtomChatFiles,
    setAtomChatImages,
    setAtomNewMessageFiles,
    setAtomNewMessageImages,
    setAtomShowFilesDisplay,
    setAtomTools,
    setAtomPlatformTools,
    setAtomSelectedTools,
    setAtomToolInUse,
    setAtomUseRetrieval,
    setAtomSourceCount,
    setAtomPromptVariables,
    setAtomShowPromptVariables,
    setAtomWorkspaceImages,
    setAtomIsPaywallOpen
  ])

  return children
}

// Main Jotai provider component that wraps the app and contains all Providers
export function JotaiProvider({ children }: { children: React.ReactNode }) {
  useRootMountMarker('jotai-provider')

  return (
    <Provider>
      <FeatureFlagsProvider>
        <ContextToJotaiSyncProvider>{children}</ContextToJotaiSyncProvider>
      </FeatureFlagsProvider>
    </Provider>
  )
}

/**
 * The FlagsInitializer has been removed to prevent initialization issues on iPad/iOS
 * Feature flag initialization is now handled in components/providers/FeatureFlagsProvider.tsx
 * with proper error handling and fallback mechanisms.
 */
